import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { 
  Filter, Grid, List, ChevronDown, X, 
  Star, Truck, Package, Award, Sliders,
  DollarSign, Tag, Zap, Heart
} from 'lucide-react';
import ProductCard from '../components/products/ProductCard';
import dataService from '../services/api';
import { Drawer, Button, Slider, Checkbox } from 'antd';

// Add Search icon import at top
import { Search } from 'lucide-react';
const AllProducts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
 
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allProducts, setAllProducts] = useState([]);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedOccasion, setSelectedOccasion] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [sortBy, setSortBy] = useState('popularity');
  const [selectedTags, setSelectedTags] = useState([]);

  // Available tags from products
  const [availableTags, setAvailableTags] = useState([]);

  // Reset when route changes
  useEffect(() => {
    const search = searchParams.get('search') || '';
    const category = searchParams.get('category') || '';
    const occasion = searchParams.get('occasion') || '';
    const type = searchParams.get('type') || '';
    const minPrice = searchParams.get('minPrice') || 0;
    const maxPrice = searchParams.get('maxPrice') || 5000;
    const sort = searchParams.get('sort') || 'popularity';

    setSearchTerm(search);
    setSelectedCategory(category);
    setSelectedOccasion(occasion);
    setSelectedType(type);
    setPriceRange([parseInt(minPrice) || 0, parseInt(maxPrice) || 5000]);
    setSortBy(sort);

    // Fetch fresh data
    fetchData();
  }, [location.search]); // Reset when URL changes

  const fetchData = async () => {
    try {
      setLoading(true);
      const [productsData, combosData, categoriesData, occasionsData] = await Promise.all([
        dataService.getProducts(),
        dataService.getCombos(),
        dataService.getCategories(),
        dataService.getOccasions()
      ]);

      setProducts(productsData);
      setCombos(combosData);
      setCategories(categoriesData);
      setOccasions(occasionsData);

      // Combine all products
      const combined = [...productsData, ...combosData];
      setAllProducts(combined);

      // Extract unique tags
      const tags = new Set();
      combined.forEach(product => {
        if (product.tags && Array.isArray(product.tags)) {
          product.tags.forEach(tag => tags.add(tag));
        }
      });
      setAvailableTags(Array.from(tags));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters function with useCallback
  const applyFilters = useCallback(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedOccasion) params.set('occasion', selectedOccasion);
    if (selectedType) params.set('type', selectedType);
    if (priceRange[0] > 0) params.set('minPrice', priceRange[0]);
    if (priceRange[1] < 5000) params.set('maxPrice', priceRange[1]);
    if (sortBy && sortBy !== 'popularity') params.set('sort', sortBy);
    if (selectedTags.length > 0) params.set('tags', selectedTags.join(','));

    setSearchParams(params);
  }, [searchTerm, selectedCategory, selectedOccasion, selectedType, priceRange, sortBy, selectedTags]);

  // Apply filters when any filter changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getFilteredProducts = () => {
    let filtered = [...allProducts];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Apply occasion filter
    if (selectedOccasion) {
      filtered = filtered.filter(p => p.occasion?.includes(selectedOccasion));
    }

    // Apply type filter
    if (selectedType) {
      filtered = filtered.filter(p => p.type === selectedType);
    }

    // Apply price filter
    filtered = filtered.filter(p => 
      p.price >= priceRange[0] && p.price <= priceRange[1]
    );

    // Apply tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(p => 
        p.tags && selectedTags.some(tag => p.tags.includes(tag))
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low-high':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high-low':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'popularity':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => (b.id || 0) - (a.id || 0));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedOccasion('');
    setSelectedType('');
    setPriceRange([0, 5000]);
    setSortBy('popularity');
    setSelectedTags([]);
    setSearchParams({});
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  // Mobile drawer content
  const renderMobileFilters = () => (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-gray-900">Filters</h3>
        <button 
          onClick={() => setIsMobileFilterOpen(false)}
          className="p-1 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-pink-500"
            />
            <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <Search className="h-4 w-4 text-gray-400" />
            </button>
          </form>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.slug}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Product Type</label>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedType(selectedType === 'single' ? '' : 'single')}
              className={`flex-1 py-2 px-3 rounded-lg border text-sm ${
                selectedType === 'single' 
                  ? 'bg-pink-50 border-pink-500 text-pink-600' 
                  : 'border-gray-300 text-gray-700'
              }`}
            >
              Single
            </button>
            <button
              onClick={() => setSelectedType(selectedType === 'combo' ? '' : 'combo')}
              className={`flex-1 py-2 px-3 rounded-lg border text-sm ${
                selectedType === 'combo' 
                  ? 'bg-pink-50 border-pink-500 text-pink-600' 
                  : 'border-gray-300 text-gray-700'
              }`}
            >
              Combo
            </button>
          </div>
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price Range: ₹{priceRange[0]} - ₹{priceRange[1]}
          </label>
          <Slider
            range
            min={0}
            max={5000}
            step={100}
            value={priceRange}
            onChange={setPriceRange}
            trackStyle={[{ backgroundColor: '#ec4899' }]}
            handleStyle={[{ borderColor: '#ec4899' }]}
          />
        </div>

        {/* Tags */}
        {availableTags.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
            <div className="flex flex-wrap gap-2">
              {availableTags.slice(0, 6).map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm ${
                    selectedTags.includes(tag)
                      ? 'bg-pink-100 text-pink-600 border border-pink-200'
                      : 'bg-gray-100 text-gray-600 border border-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sort */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="popularity">Popularity</option>
            <option value="price-low-high">Price: Low to High</option>
            <option value="price-high-low">Price: High to Low</option>
            <option value="newest">Newest First</option>
            <option value="discount">Highest Discount</option>
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-8 pt-6 border-t">
        <button
          onClick={clearFilters}
          className="flex-1 py-2.5 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 text-sm"
        >
          Clear All
        </button>
        <button
          onClick={() => setIsMobileFilterOpen(false)}
          className="flex-1 py-2.5 bg-pink-600 text-white rounded-lg font-medium hover:bg-pink-700 text-sm"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 py-8">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-pink-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
        <div className="container mx-auto px-3 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Products</h1>
              <p className="text-gray-600 text-xs mt-0.5">
                {filteredProducts.length} products found
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Mobile Filter Button */}
              <button
                onClick={() => setIsMobileFilterOpen(true)}
                className="md:hidden flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <Sliders className="h-4 w-4" />
                Filters
              </button>

              {/* Desktop View Toggle */}
              <div className="hidden md:flex items-center gap-1 border border-gray-300 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-pink-50 text-pink-600' : 'text-gray-500'}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-pink-50 text-pink-600' : 'text-gray-500'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-pink-500"
              >
                <option value="popularity">Popularity</option>
                <option value="price-low-high">Price: Low to High</option>
                <option value="price-high-low">Price: High to Low</option>
                <option value="newest">Newest First</option>
                <option value="discount">Highest Discount</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 py-4">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-20">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-gray-900">Filters</h3>
                {(
                  selectedCategory || 
                  selectedOccasion || 
                  selectedType || 
                  priceRange[0] > 0 || 
                  priceRange[1] < 5000 ||
                  selectedTags.length > 0
                ) && (
                  <button
                    onClick={clearFilters}
                    className="text-pink-600 hover:text-pink-700 text-sm"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Search */}
              <div className="mb-4">
                <form onSubmit={handleSearch} className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search products..."
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-pink-500"
                  />
                  <button type="submit" className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <Search className="h-4 w-4 text-gray-400" />
                  </button>
                </form>
              </div>

              {/* Category */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Type</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedType === 'single'}
                      onChange={(e) => setSelectedType(e.target.checked ? 'single' : '')}
                      className="h-4 w-4 text-pink-600 rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Single Products</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedType === 'combo'}
                      onChange={(e) => setSelectedType(e.target.checked ? 'combo' : '')}
                      className="h-4 w-4 text-pink-600 rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Combos</span>
                  </label>
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Range: ₹{priceRange[0]} - ₹{priceRange[1]}
                </label>
                <Slider
                  range
                  min={0}
                  max={5000}
                  step={100}
                  value={priceRange}
                  onChange={setPriceRange}
                  trackStyle={[{ backgroundColor: '#ec4899' }]}
                  handleStyle={[{ borderColor: '#ec4899' }]}
                />
              </div>

              {/* Tags */}
              {availableTags.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Popular Tags</label>
                  <div className="flex flex-wrap gap-2">
                    {availableTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => handleTagToggle(tag)}
                        className={`px-3 py-1.5 rounded-full text-xs ${
                          selectedTags.includes(tag)
                            ? 'bg-pink-100 text-pink-600 border border-pink-200'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Filters */}
              {(
                selectedCategory || 
                priceRange[0] > 0 || 
                priceRange[1] < 5000 ||
                selectedTags.length > 0
              ) && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Active Filters</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCategory && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-50 text-pink-600 rounded text-xs">
                        {selectedCategory}
                        <button onClick={() => setSelectedCategory('')}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {priceRange[0] > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                        Min: ₹{priceRange[0]}
                        <button onClick={() => setPriceRange([0, priceRange[1]])}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {priceRange[1] < 5000 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs">
                        Max: ₹{priceRange[1]}
                        <button onClick={() => setPriceRange([priceRange[0], 5000])}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {selectedTags.map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        {tag}
                        <button onClick={() => handleTagToggle(tag)}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Products Section */}
          <div className="flex-1">
            {/* Products Grid/List */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                {filteredProducts.map(product => (
                  <ProductCard
                    key={`${product.type}-${product.id}`}
                    product={product}
                    type={product.type}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredProducts.map(product => (
                  <div key={`${product.type}-${product.id}`} className="bg-white rounded-xl shadow-sm p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="md:w-48 md:h-48 w-full h-48">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{product.name}</h3>
                            <p className="text-gray-600 text-sm mt-1 line-clamp-2">{product.description}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-pink-600">₹{product.price}</p>
                            {product.originalPrice && (
                              <p className="text-gray-400 line-through text-sm">₹{product.originalPrice}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4">
                          <Link 
                            to={`/product/${product.type}/${product.id}`}
                            className="bg-pink-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-pink-700 text-sm inline-block"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your filters or search criteria</p>
                <button
                  onClick={clearFilters}
                  className="bg-pink-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-pink-700 text-sm"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <Drawer
        title="Filters"
        placement="right"
        onClose={() => setIsMobileFilterOpen(false)}
        open={isMobileFilterOpen}
        width={320}
        closeIcon={<X className="h-4 w-4" />}
        headerStyle={{ padding: '16px' }}
      >
        {renderMobileFilters()}
      </Drawer>
    </div>
  );
};

export default AllProducts;
